import { useEffect, useRef } from 'react';

/**
 * Shared modal dialog accessibility behaviour (BugHerd #132).
 *
 * Several overlays hand-roll their own markup instead of using the shared Modal
 * primitive, so the behaviour lives here rather than in a component: any overlay
 * can adopt correct semantics without restructuring its layout.
 *
 * Deliberately fixes four defects in the original Modal implementation rather
 * than carrying them over:
 *
 *  1. It re-captured the invoking element on every effect run. Because the effect
 *     depended on `onClose`, any parent re-render with a non-memoised handler
 *     re-captured an element *inside* the dialog, so closing restored focus to a
 *     node that no longer existed and focus silently fell to <body>. The invoker
 *     is now captured exactly once per open, and handlers are held in refs.
 *  2. The Tab trap was a React onKeyDown on the panel, so it never fired once
 *     focus was outside the dialog - clicking the backdrop dropped focus to
 *     <body> and Tab then walked the whole background page. The listener is now
 *     document-level and pulls stray focus back in.
 *  3. Closing a stacked dialog released the body scroll lock and Escape closed
 *     every open dialog at once. A module-level stack means only the topmost
 *     dialog reacts to Escape, and the lock is reference counted.
 *  4. Focus was restored only when `isOpen` flipped to false, so unmounting while
 *     open (a route change) never restored it. Restoration now happens in cleanup.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Rendered and not visually hidden, so genuinely reachable by keyboard. */
function isReachable(el: HTMLElement): boolean {
  return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
}

function focusablesWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isReachable);
}

// Module-level so stacked dialogs cooperate instead of fighting.
const dialogStack: symbol[] = [];
let scrollLockCount = 0;

function lockScroll() {
  if (scrollLockCount === 0) document.body.style.overflow = 'hidden';
  scrollLockCount += 1;
}

function releaseScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) document.body.style.overflow = '';
}

export interface UseModalA11yOptions {
  /** Whether the dialog is currently rendered. */
  isOpen: boolean;
  /** Invoked when Escape is pressed on the topmost dialog. */
  onClose: () => void;
  /** The dialog panel - the element carrying role="dialog". */
  containerRef: React.RefObject<HTMLElement | null>;
  /** When false, Escape does not close. Defaults to true. */
  closable?: boolean;
  /** Element to focus on open. Defaults to the panel itself. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Element to mark inert while open. Only safe when the dialog is portalled
   * outside this element - an inline overlay would inert itself.
   */
  inertRef?: React.RefObject<HTMLElement | null>;
}

export function useModalA11y({
  isOpen,
  onClose,
  containerRef,
  closable = true,
  initialFocusRef,
  inertRef,
}: UseModalA11yOptions): void {
  // Held in refs so the effect can depend on `isOpen` alone; otherwise a parent
  // re-render would re-run setup and re-capture the invoker from inside the dialog.
  const onCloseRef = useRef(onClose);
  const closableRef = useRef(closable);
  onCloseRef.current = onClose;
  closableRef.current = closable;

  useEffect(() => {
    if (!isOpen) return;

    const token = Symbol('dialog');
    dialogStack.push(token);
    const isTopmost = () => dialogStack[dialogStack.length - 1] === token;

    const invoker = document.activeElement as HTMLElement | null;
    lockScroll();

    const inertTarget = inertRef?.current ?? null;
    if (inertTarget) inertTarget.setAttribute('inert', '');

    // Move focus in. Retry once on the next frame: a panel that animates in can
    // still be unfocusable on the first frame, and a silently dropped focus()
    // is the whole defect this ticket is about.
    let frame = 0;
    const focusIn = (attempt: number) => {
      const container = containerRef.current;
      if (!container) return;
      const target = initialFocusRef?.current ?? container;
      target.focus({ preventScroll: true });
      if (!container.contains(document.activeElement) && attempt < 2) {
        frame = requestAnimationFrame(() => focusIn(attempt + 1));
      }
    };
    frame = requestAnimationFrame(() => focusIn(0));

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost()) return;

      if (event.key === 'Escape') {
        if (closableRef.current) {
          event.preventDefault();
          onCloseRef.current();
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const container = containerRef.current;
      if (!container) return;
      const focusables = focusablesWithin(container);
      const active = document.activeElement as HTMLElement | null;

      // Focus escaped the dialog entirely (typically a backdrop click). Pull it back.
      if (!container.contains(active)) {
        event.preventDefault();
        (focusables[event.shiftKey ? focusables.length - 1 : 0] ?? container).focus({ preventScroll: true });
        return;
      }

      if (focusables.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);

      const index = dialogStack.indexOf(token);
      if (index !== -1) dialogStack.splice(index, 1);

      releaseScroll();
      if (inertTarget) inertTarget.removeAttribute('inert');

      // Cleanup rather than an `isOpen === false` branch, so unmounting while
      // open (for example a route change) still returns focus to the invoker.
      if (invoker && document.contains(invoker)) {
        invoker.focus({ preventScroll: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}
